import React from 'react';
import GroupPermissionGrid from 'components/molecules/GroupPermissionGrid';
import { IData, IRow } from 'components/molecules/GroupPermissionGrid/types';
import { useQuery } from 'react-query';
import qs from 'querystring';
import { useSelectFilter } from 'hooks/planning/useFilter';
import { postGroupPermissionGrid } from 'services/msgraph-users';
import { GROUP_PERMISSION_GRID_VALIDATION_RULES } from 'constants/grid/validationRules';
import { openInNewTab } from 'helpers';
import { postExportGroupPermissionGrid } from 'services/msgraph-group';
import { columns } from './data';

const TAKE = 10;
const MAX_ROWS = 100000;

const mapRows = (result: IRow[]) => {
  return result.map(row => {
    return {
      id: row.id,
      name: row.name,
      group: row.group,
      modifiedDate: row.modifiedDate,
      modifiedUser: row.modifiedUser,
    };
  });
};

const GroupPermissionGridContainer = () => {
  // INÍCIO ESTADO DOS FILTROS
  const [userNameFilterOptionsContext, userGroupFilterOptionsContext] = [
    useSelectFilter('userName'),
    useSelectFilter('userGroup'),
  ];

  const [exportingData, setExportingData] = React.useState<boolean>(false);

  const allFilterContexts = React.useMemo(
    () => [userNameFilterOptionsContext, userGroupFilterOptionsContext],
    [userGroupFilterOptionsContext, userNameFilterOptionsContext],
  );
  // FIM ESTADO DOS FILTROS

  // INÍCIO IDS FILTROS
  const userName = React.useMemo(
    () => userNameFilterOptionsContext.map(item => String(item?.label)),
    [userNameFilterOptionsContext],
  );
  const userGroup = React.useMemo(
    () => userGroupFilterOptionsContext.map(item => String(item?.label)),
    [userGroupFilterOptionsContext],
  );
  const userIds = React.useMemo(
    () => userNameFilterOptionsContext.map(item => Number(item.value)),
    [userNameFilterOptionsContext],
  );
  const groupIds = React.useMemo(
    () => userGroupFilterOptionsContext.map(item => Number(item.value)),
    [userGroupFilterOptionsContext],
  );
  // FIM IDS DOS FILTROS

  // INÍCIO ESTADO DAS LINHAS DO GRID
  const [gridRows, setGridRows] = React.useState<IRow[]>([]);
  // FIM ESTADO DAS LINHAS DO GRID

  // INÍCIO ESTADO DOS ARGUMENTOS DE PAGINAÇÃO DO GRID
  const [totalRows, setTotalRows] = React.useState(MAX_ROWS);
  const [currentPage, setCurrentPage] = React.useState<number>(0);
  const [pageSize, setPageSize] = React.useState<number>(TAKE);
  const pageSizes = React.useMemo(() => [10, 20, 30, 40, 50, 100], []);
  // FIM ESTADO DOS ARGUMENTOS DE PAGINAÇÃO DO GRID

  // INÍCIO ESTADO DAS CONFIGURAÇÕES DAS GRID
  const dataGrid = React.useMemo<IData[]>(() => columns, []);
  // FIM ESTADO DA CONFIGURAÇÕES DO GRID

  const exportData = React.useCallback(() => {
    setExportingData(true);

    const params = {
      userIds,
      groupIds,
    };

    postExportGroupPermissionGrid(params)
      .then(({ url }) => {
        openInNewTab(url);
      })
      .finally(() => setExportingData(false));
  }, [groupIds, userIds]);

  // AO ALTERAR OS FILTROS, DEVERÁ RETORNAR PARA PÁGINA INICIAL
  React.useEffect(() => {
    setCurrentPage(0);
  }, [allFilterContexts, pageSize]);

  const queryGroupPermissionGrid = useQuery({
    queryKey: [
      'GroupPermissionDataContext',
      [pageSize, currentPage, ...allFilterContexts],
    ],
    queryFn: async () => {
      const qsparams = qs.stringify({
        page: currentPage + 1,
        take: pageSize,
      });

      const params = { userName, userGroup };

      const { data } = await postGroupPermissionGrid(qsparams, params);
      return data;
    },
    select: data => {
      const rows: IRow[] = mapRows(data.result);
      return {
        count: data.count,
        rows,
      };
    },
    onSuccess: ({ count, rows }: { rows: IRow[]; count: number }) => {
      setGridRows(rows);
      setTotalRows(count);
    },
    enabled: true,
    cacheTime: 0,
  });

  const getRowId = React.useCallback((row: IRow) => String(row.id), []);

  const getRowById = React.useCallback(
    id => {
      const strId = `${id}`;
      return gridRows.find(row => `${row?.id}` === strId);
    },
    [gridRows],
  );

  const refetch = React.useCallback(() => {
    queryGroupPermissionGrid.refetch();
  }, [queryGroupPermissionGrid]);

  return (
    <GroupPermissionGrid
      columns={dataGrid}
      rows={gridRows}
      loading={
        exportingData ||
        queryGroupPermissionGrid?.isLoading ||
        queryGroupPermissionGrid?.isFetching
      }
      getRowId={getRowId}
      getRowById={getRowById}
      currentPage={currentPage}
      pageSize={pageSize}
      pageSizes={pageSizes}
      totalCount={totalRows}
      onCurrentPageChange={setCurrentPage}
      onPageSizeChange={setPageSize}
      onExport={exportData}
      dateTimeCells={['modifiedDate']}
      validationRules={GROUP_PERMISSION_GRID_VALIDATION_RULES}
      refetch={refetch}
    />
  );
};

export default GroupPermissionGridContainer;
